import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Box,
  Grid,
  FormControl,
  Select,
  InputAdornment,
} from '@mui/material';
import PriceField from './PriceField';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

function AddMedium({ open, onClose, onAdd, editMode = false, mediumToEdit = {}, setSuccess, setError }) {
  const { user, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const defaultCurrency = farmData?.settings?.currency || '\u00A3';
  const defaultUnit = farmData?.settings?.weightUnit || 'g';
  const inlineSelectSx = {
    fontSize: '0.85rem',
    opacity: 0.85,
    '&:hover': { opacity: 1 },
    '&.Mui-focused': { opacity: 1 },
    '& .MuiSelect-icon': { fontSize: 18 },
  };

  const renderInlineSelect = (value, onChange, options) => (
    <Select
      variant="standard"
      disableUnderline
      sx={inlineSelectSx}
      value={value}
      onChange={onChange}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const [newMedium, setNewMedium] = useState({
    brand: '',
    packetWeight: '',
    weightUnit: defaultUnit,
    packetCost: '',
    costCurrency: defaultCurrency,
    notes: '',
  });

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editMode && mediumToEdit && mediumToEdit.id) {
      setNewMedium({
        brand: mediumToEdit.brand || '',
        packetWeight: mediumToEdit.packetWeight || '',
        weightUnit: mediumToEdit.weightUnit || defaultUnit,
        packetCost: mediumToEdit.packetCost || '',
        costCurrency: mediumToEdit.costCurrency || defaultCurrency,
        notes: mediumToEdit.notes || '',
      });
    } else {
      setNewMedium({
        brand: '',
        packetWeight: '',
        weightUnit: defaultUnit,
        packetCost: '',
        costCurrency: defaultCurrency,
        notes: '',
      });
    }

    setFormSubmitted(false);
    setTouched({});
  }, [open, editMode, mediumToEdit, defaultCurrency, defaultUnit]);

  const handleSave = async () => {
    setFormSubmitted(true);
    if (!newMedium.brand) {
      setError?.('Please fill in required field: Name/Brand');
      return;
    }
    try {
      if (!user?.uid) {
        setError?.('User not logged in.');
        return;
      }

      if (!activeFarmId) {
        setError?.('No active farm selected.');
        return;
      }

      if (editMode && mediumToEdit?.id) {
        await updateDoc(doc(db, 'farms', activeFarmId, 'media', mediumToEdit.id), newMedium);
        setSuccess?.('Medium updated successfully.');
      } else {
        await addDoc(collection(db, 'farms', activeFarmId, 'media'), newMedium);
        setSuccess?.('Medium added successfully.');
      }
      onAdd?.(newMedium);
      handleCloseDialog();
    } catch (error) {
      setError?.('Error saving medium.');
    }
  };

  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const isError = (field) => {
    return !newMedium[field] && (touched[field] || formSubmitted);
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
      <DialogTitle>{editMode ? 'Edit Medium' : 'Add New Medium'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              label="Name / Brand"
              fullWidth
              required
              value={newMedium.brand}
              onChange={(e) => setNewMedium({ ...newMedium, brand: e.target.value })}
              onBlur={() => handleBlur('brand')}
              error={isError('brand')}
              helperText={isError('brand') ? 'This field is required' : ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Packet Weight"
              fullWidth
              value={newMedium.packetWeight}
              onChange={(e) => setNewMedium({ ...newMedium, packetWeight: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {renderInlineSelect(
                      newMedium.weightUnit,
                      (e) => setNewMedium({ ...newMedium, weightUnit: e.target.value }),
                      [
                        { value: 'g', label: 'g' },
                        { value: 'kg', label: 'kg' },
                        { value: 'oz', label: 'oz' },
                        { value: 'lb', label: 'lb' },
                      ]
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PriceField
              label="Packet Cost"
              fullWidth
              value={newMedium.packetCost}
              onChange={(formattedValue) => setNewMedium({ ...newMedium, packetCost: formattedValue })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {renderInlineSelect(
                      newMedium.costCurrency,
                      (e) => setNewMedium({ ...newMedium, costCurrency: e.target.value }),
                      [
                        { value: '\u00A3', label: '\u00A3' },
                        { value: '$', label: '$' },
                        { value: '\u20AC', label: '\u20AC' },
                      ]
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <TextField
          margin="dense"
          label="Notes"
          fullWidth
          multiline
          minRows={2}
          value={newMedium.notes}
          onChange={(e) => setNewMedium({ ...newMedium, notes: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          {editMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddMedium;



